import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MagnifyingGlass, CreditCard, CheckCircle, XCircle,
  PencilSimple, ArrowLeft, User, Phone, Envelope,
  Buildings, Receipt, Package, ChartBar, MapPin,
} from "@phosphor-icons/react";

const fmtMoney = (v) => `$${(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CREDIT_DAYS_OPTIONS = [7, 15, 30, 45, 60];

const SAT_FORMAS_PAGO = [
  { code: "", label: "-- Seleccionar --" },
  { code: "01", label: "01 - Efectivo" },
  { code: "02", label: "02 - Cheque nominativo" },
  { code: "03", label: "03 - Transferencia electronica" },
  { code: "04", label: "04 - Tarjeta de credito" },
  { code: "05", label: "05 - Monedero electronico" },
  { code: "06", label: "06 - Dinero electronico" },
  { code: "28", label: "28 - Tarjeta de debito" },
  { code: "29", label: "29 - Tarjeta de servicios" },
  { code: "99", label: "99 - Por definir" },
];

const SAT_METODOS_PAGO = [
  { code: "", label: "-- Seleccionar --" },
  { code: "PUE", label: "PUE - Pago en Una Exhibicion" },
  { code: "PPD", label: "PPD - Pago en Parcialidades o Diferido" },
];

const SAT_USO_CFDI = [
  { code: "", label: "-- Seleccionar --" },
  { code: "G01", label: "G01 - Adquisicion de mercancias" },
  { code: "G02", label: "G02 - Devoluciones, descuentos o bonificaciones" },
  { code: "G03", label: "G03 - Gastos en general" },
  { code: "I01", label: "I01 - Construcciones" },
  { code: "I02", label: "I02 - Mobiliario y equipo de oficina" },
  { code: "I03", label: "I03 - Equipo de transporte" },
  { code: "I04", label: "I04 - Equipo de computo y accesorios" },
  { code: "I08", label: "I08 - Otra maquinaria y equipo" },
  { code: "P01", label: "P01 - Por definir" },
  { code: "S01", label: "S01 - Sin efectos fiscales" },
  { code: "CP01", label: "CP01 - Pagos" },
];

const SAT_REGIMENES = [
  { code: "", label: "-- Seleccionar --" },
  { code: "601", label: "601 - General de Ley PM" },
  { code: "603", label: "603 - Personas Morales sin fines de lucro" },
  { code: "605", label: "605 - Sueldos y salarios" },
  { code: "606", label: "606 - Arrendamiento" },
  { code: "607", label: "607 - Regimen de Enajenacion" },
  { code: "608", label: "608 - Demas ingresos" },
  { code: "610", label: "610 - Residentes en el extranjero" },
  { code: "612", label: "612 - Personas Fisicas con Actividad Empresarial" },
  { code: "616", label: "616 - Sin obligaciones fiscales" },
  { code: "620", label: "620 - Sociedades Cooperativas de Produccion" },
  { code: "621", label: "621 - Incorporacion Fiscal" },
  { code: "625", label: "625 - Regimen de Actividades Agricolas" },
  { code: "626", label: "626 - RESICO" },
];

export function CreditClientsModule() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cxc/clients`, { headers });
      setClients(res.data);
    } catch { toast.error("Error al cargar clientes"); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const filtered = clients.filter(c => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (c.company_name || "").toLowerCase().includes(s) ||
           (c.contact_name || "").toLowerCase().includes(s) ||
           (c.rfc || "").toLowerCase().includes(s);
  });

  const totalCartera = clients.reduce((s, c) => s + (c.total_due || 0), 0);
  const totalOverdue = clients.reduce((s, c) => s + (c.overdue || 0), 0);
  const withCredit = clients.filter(c => c.credit_enabled).length;

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-sky-600 border-t-transparent rounded-full" /></div>;

  // Detail view
  if (selectedClient) {
    return <ClientCreditDetail clientId={selectedClient} headers={headers} isAdmin={isAdmin} onBack={() => { setSelectedClient(null); fetchClients(); }} />;
  }

  return (
    <div className="space-y-5" data-testid="credit-clients-module">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <CreditCard size={24} weight="duotone" className="text-sky-600" />
          Clientes - Autorizacion de Credito
        </h1>
        <p className="text-sm text-slate-500">Gestiona terminos de credito, datos fiscales y contacto de cobranza</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border rounded-xl p-4">
          <p className="text-[10px] text-slate-400 uppercase font-bold">Total Clientes</p>
          <p className="text-2xl font-extrabold text-slate-900">{clients.length}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-[10px] text-emerald-500 uppercase font-bold">Con Credito</p>
          <p className="text-2xl font-extrabold text-emerald-700">{withCredit}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-[10px] text-sky-500 uppercase font-bold">Cartera Total</p>
          <p className="text-xl font-extrabold text-sky-700 font-mono">{fmtMoney(totalCartera)}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-[10px] text-red-500 uppercase font-bold">Vencido</p>
          <p className="text-xl font-extrabold text-red-700 font-mono">{fmtMoney(totalOverdue)}</p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar cliente, RFC..." className="h-9 text-sm pl-8" data-testid="credit-search" />
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="credit-clients-table">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-left">Cliente</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-left">RFC</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Credito</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Limite</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Plazo</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Saldo</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Disponible</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Metodo Pago</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className={`border-b hover:bg-sky-50/30 cursor-pointer transition ${c.credit_blocked ? "bg-red-50/30" : ""}`} onClick={() => setSelectedClient(c.id)} data-testid={`client-row-${c.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 text-xs font-bold shrink-0">
                        {(c.company_name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-slate-800">{c.company_name}</span>
                        {c.contact_name && <p className="text-[10px] text-slate-400">{c.contact_name}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono text-slate-600">{c.rfc || "-"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {c.credit_enabled ? <CheckCircle size={18} weight="fill" className="text-emerald-500 mx-auto" /> : <XCircle size={18} weight="fill" className="text-slate-300 mx-auto" />}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtMoney(c.credit_limit)}</td>
                  <td className="px-3 py-2.5 text-center text-xs text-slate-600">{c.credit_days}d</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs font-bold text-slate-900">{c.total_due > 0 ? fmtMoney(c.total_due) : "-"}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-emerald-600">{c.credit_enabled ? fmtMoney(c.available_credit) : "-"}</td>
                  <td className="px-3 py-2.5 text-center text-[10px] text-slate-500">{c.sat_metodo_pago || "-"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {c.credit_blocked ? <Badge className="bg-red-100 text-red-700 border-red-200 text-[9px]">BLOQUEADO</Badge>
                    : c.credit_enabled ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px]">ACTIVO</Badge>
                    : <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[9px]">SIN CREDITO</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin clientes encontrados</p>}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════ CLIENT CREDIT DETAIL ═══════════════════════════

function ClientCreditDetail({ clientId, headers, isAdmin, onBack }) {
  const [client, setClient] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [expRes, anaRes] = await Promise.all([
        axios.get(`${API}/cxc/clients/${clientId}`, { headers }),
        axios.get(`${API}/cxc/clients/${clientId}/analytics`, { headers }),
      ]);
      const c = expRes.data.client;
      setClient({ ...c, summary: expRes.data.summary });
      setAnalytics(anaRes.data);
      setForm({
        credit_limit: c.credit_limit || 0,
        credit_days: c.credit_days || 30,
        credit_enabled: c.credit_enabled || false,
        credit_blocked: c.credit_blocked || false,
        rfc: c.rfc || "",
        sat_forma_pago: c.sat_forma_pago || "",
        sat_metodo_pago: c.sat_metodo_pago || "",
        sat_uso_cfdi: c.sat_uso_cfdi || "",
        sat_regimen_fiscal: c.sat_regimen_fiscal || "",
        billing_contact: c.billing_contact || { name: "", position: "", phone: "", email: "" },
        fiscal_address: c.fiscal_address || { street: "", ext_number: "", int_number: "", neighborhood: "", postal_code: "", municipality: "", state: "", country: "Mexico" },
      });
    } catch { toast.error("Error al cargar cliente"); }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    try {
      await axios.put(`${API}/cxc/clients/${clientId}/credit`, form, { headers });
      toast.success("Cliente actualizado");
      setEditing(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleExportHistory = async (fmt) => {
    try {
      const res = await axios.get(`${API}/cxc/clients/${clientId}/export-history/${fmt}`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Historial_Compras.${fmt === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Historial exportado");
    } catch { toast.error("Error al exportar"); }
  };

  const updateBillingContact = (field, value) => {
    setForm(prev => ({ ...prev, billing_contact: { ...prev.billing_contact, [field]: value } }));
  };

  const updateFiscalAddress = (field, value) => {
    setForm(prev => ({ ...prev, fiscal_address: { ...prev.fiscal_address, [field]: value } }));
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-sky-600 border-t-transparent rounded-full" /></div>;
  if (!client) return <p className="text-center text-slate-500 py-12">Cliente no encontrado</p>;

  const bc = client.billing_contact || {};

  return (
    <div className="space-y-5" data-testid="client-credit-detail">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-sky-600 transition" data-testid="back-to-list">
          <ArrowLeft size={16} /> Clientes
        </button>
        <h2 className="text-lg font-bold text-slate-900">{client.company_name}</h2>
        {isAdmin && !editing && (
          <Button size="sm" variant="outline" className="ml-auto text-xs text-sky-600 border-sky-300" onClick={() => setEditing(true)} data-testid="edit-all-btn">
            <PencilSimple size={14} className="mr-1" /> Editar
          </Button>
        )}
        {editing && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setEditing(false)}>Cancelar</Button>
            <Button size="sm" className="text-xs bg-sky-600 hover:bg-sky-700 text-white" onClick={save} data-testid="save-all-btn">Guardar Todo</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Credit + Fiscal */}
        <div className="space-y-4">
          {/* Credit Config */}
          <div className="bg-white border rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><CreditCard size={16} className="text-sky-600" /> Condiciones de Credito</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Limite de Credito" value={editing ?
                <Input type="number" value={form.credit_limit} onChange={e => setForm(p => ({ ...p, credit_limit: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm font-mono" data-testid="f-credit-limit" />
                : <span className="font-mono font-bold">{fmtMoney(client.credit_limit)}</span>
              } />
              <Field label="Plazo (dias)" value={editing ?
                <select value={form.credit_days} onChange={e => setForm(p => ({ ...p, credit_days: parseInt(e.target.value) }))} className="w-full h-8 border rounded-md text-sm px-2" data-testid="f-credit-days">
                  {CREDIT_DAYS_OPTIONS.map(d => <option key={d} value={d}>{d} dias</option>)}
                </select>
                : <span className="font-bold">{client.credit_days} dias</span>
              } />
              <Field label="Credito Activo" value={editing ?
                <input type="checkbox" checked={form.credit_enabled} onChange={e => setForm(p => ({ ...p, credit_enabled: e.target.checked }))} className="w-4 h-4 rounded mt-1" data-testid="f-credit-enabled" />
                : <span className={client.credit_enabled ? "text-emerald-600 font-bold" : "text-slate-400"}>
                    {client.credit_enabled ? "Si" : "No"}
                  </span>
              } />
              <Field label="Bloqueado" value={editing ?
                <input type="checkbox" checked={form.credit_blocked} onChange={e => setForm(p => ({ ...p, credit_blocked: e.target.checked }))} className="w-4 h-4 rounded accent-red-600 mt-1" data-testid="f-credit-blocked" />
                : client.credit_blocked ? <Badge className="bg-red-100 text-red-700 text-[9px]">BLOQUEADO</Badge> : <span className="text-slate-400">No</span>
              } />
            </div>
            {/* Balance summary */}
            {client.summary && (
              <div className="flex gap-3 mt-3 pt-3 border-t">
                <div className="text-center flex-1">
                  <p className="text-[9px] text-red-500 uppercase font-bold">Saldo</p>
                  <p className="text-sm font-bold text-red-700 font-mono">{fmtMoney(client.summary.total_due)}</p>
                </div>
                <div className="text-center flex-1">
                  <p className="text-[9px] text-amber-500 uppercase font-bold">Vencido</p>
                  <p className="text-sm font-bold text-amber-700 font-mono">{fmtMoney(client.summary.overdue)}</p>
                </div>
                <div className="text-center flex-1">
                  <p className="text-[9px] text-emerald-500 uppercase font-bold">Disponible</p>
                  <p className="text-sm font-bold text-emerald-700 font-mono">{fmtMoney(client.summary.available_credit)}</p>
                </div>
              </div>
            )}
          </div>

          {/* SAT Fiscal Data */}
          <div className="bg-white border rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><Receipt size={16} className="text-blue-600" /> Datos Fiscales SAT</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="RFC" value={editing ?
                <Input value={form.rfc} onChange={e => setForm(p => ({ ...p, rfc: e.target.value.toUpperCase() }))} className="h-8 text-sm font-mono" maxLength={13} data-testid="f-rfc" placeholder="XAXX010101000" />
                : <span className="font-mono text-xs">{client.rfc || "-"}</span>
              } />
              <Field label="Regimen Fiscal" value={editing ?
                <select value={form.sat_regimen_fiscal} onChange={e => setForm(p => ({ ...p, sat_regimen_fiscal: e.target.value }))} className="w-full h-8 border rounded-md text-xs px-2" data-testid="f-regimen">
                  {SAT_REGIMENES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
                : <span className="text-xs">{SAT_REGIMENES.find(r => r.code === client.sat_regimen_fiscal)?.label || client.sat_regimen_fiscal || "-"}</span>
              } />
              <Field label="Forma de Pago" value={editing ?
                <select value={form.sat_forma_pago} onChange={e => setForm(p => ({ ...p, sat_forma_pago: e.target.value }))} className="w-full h-8 border rounded-md text-xs px-2" data-testid="f-forma-pago">
                  {SAT_FORMAS_PAGO.map(f => <option key={f.code} value={f.code}>{f.label}</option>)}
                </select>
                : <span className="text-xs">{SAT_FORMAS_PAGO.find(f => f.code === client.sat_forma_pago)?.label || client.sat_forma_pago || "-"}</span>
              } />
              <Field label="Metodo de Pago" value={editing ?
                <select value={form.sat_metodo_pago} onChange={e => setForm(p => ({ ...p, sat_metodo_pago: e.target.value }))} className="w-full h-8 border rounded-md text-xs px-2" data-testid="f-metodo-pago">
                  {SAT_METODOS_PAGO.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
                </select>
                : <span className="text-xs">{SAT_METODOS_PAGO.find(m => m.code === client.sat_metodo_pago)?.label || client.sat_metodo_pago || "-"}</span>
              } />
              <div className="col-span-2">
                <Field label="Uso de CFDI" value={editing ?
                  <select value={form.sat_uso_cfdi} onChange={e => setForm(p => ({ ...p, sat_uso_cfdi: e.target.value }))} className="w-full h-8 border rounded-md text-xs px-2" data-testid="f-uso-cfdi">
                    {SAT_USO_CFDI.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
                  </select>
                  : <span className="text-xs">{SAT_USO_CFDI.find(u => u.code === client.sat_uso_cfdi)?.label || client.sat_uso_cfdi || "-"}</span>
                } />
              </div>
            </div>
          </div>

          {/* Billing Contact */}
          <div className="bg-white border rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><User size={16} className="text-violet-600" /> Contacto de Cobranza</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nombre" value={editing ?
                <Input value={form.billing_contact?.name || ""} onChange={e => updateBillingContact("name", e.target.value)} className="h-8 text-sm" data-testid="f-bc-name" placeholder="Nombre completo" />
                : <span className="text-xs">{bc.name || "-"}</span>
              } />
              <Field label="Puesto" value={editing ?
                <Input value={form.billing_contact?.position || ""} onChange={e => updateBillingContact("position", e.target.value)} className="h-8 text-sm" data-testid="f-bc-position" placeholder="Ej: Cuentas por pagar" />
                : <span className="text-xs">{bc.position || "-"}</span>
              } />
              <Field label="Telefono" value={editing ?
                <Input value={form.billing_contact?.phone || ""} onChange={e => updateBillingContact("phone", e.target.value)} className="h-8 text-sm" data-testid="f-bc-phone" placeholder="668-123-4567" />
                : <span className="text-xs flex items-center gap-1">{bc.phone ? <><Phone size={12} />{bc.phone}</> : "-"}</span>
              } />
              <Field label="Correo" value={editing ?
                <Input value={form.billing_contact?.email || ""} onChange={e => updateBillingContact("email", e.target.value)} className="h-8 text-sm" data-testid="f-bc-email" placeholder="pagos@empresa.com" />
                : <span className="text-xs flex items-center gap-1">{bc.email ? <><Envelope size={12} />{bc.email}</> : "-"}</span>
              } />
            </div>
          </div>

          {/* Fiscal Address */}
          <div className="bg-white border rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><MapPin size={16} className="text-emerald-600" /> Domicilio Fiscal</h3>
            {editing ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Calle" value={<Input value={form.fiscal_address?.street || ""} onChange={e => updateFiscalAddress("street", e.target.value)} className="h-8 text-sm" data-testid="f-fa-street" placeholder="Nombre de la calle" />} />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="No. Exterior" value={<Input value={form.fiscal_address?.ext_number || ""} onChange={e => updateFiscalAddress("ext_number", e.target.value)} className="h-8 text-sm" data-testid="f-fa-ext" placeholder="123" />} />
                  <Field label="No. Interior" value={<Input value={form.fiscal_address?.int_number || ""} onChange={e => updateFiscalAddress("int_number", e.target.value)} className="h-8 text-sm" data-testid="f-fa-int" placeholder="A (opcional)" />} />
                </div>
                <Field label="Colonia" value={<Input value={form.fiscal_address?.neighborhood || ""} onChange={e => updateFiscalAddress("neighborhood", e.target.value)} className="h-8 text-sm" data-testid="f-fa-neighborhood" placeholder="Colonia" />} />
                <Field label="Codigo Postal" value={<Input value={form.fiscal_address?.postal_code || ""} onChange={e => updateFiscalAddress("postal_code", e.target.value)} className="h-8 text-sm" data-testid="f-fa-cp" placeholder="81200" maxLength={5} />} />
                <Field label="Municipio / Delegacion" value={<Input value={form.fiscal_address?.municipality || ""} onChange={e => updateFiscalAddress("municipality", e.target.value)} className="h-8 text-sm" data-testid="f-fa-municipality" placeholder="Los Mochis" />} />
                <Field label="Estado" value={<Input value={form.fiscal_address?.state || ""} onChange={e => updateFiscalAddress("state", e.target.value)} className="h-8 text-sm" data-testid="f-fa-state" placeholder="Sinaloa" />} />
                <Field label="Pais" value={<Input value={form.fiscal_address?.country || "Mexico"} onChange={e => updateFiscalAddress("country", e.target.value)} className="h-8 text-sm" data-testid="f-fa-country" />} />
              </div>
            ) : (
              (() => {
                const fa = client.fiscal_address || {};
                const hasAddr = fa.street || fa.neighborhood || fa.postal_code;
                if (!hasAddr) return <p className="text-xs text-slate-400">Sin domicilio fiscal registrado</p>;
                const line1 = [fa.street, fa.ext_number ? `#${fa.ext_number}` : "", fa.int_number ? `Int. ${fa.int_number}` : ""].filter(Boolean).join(" ");
                const line2 = [fa.neighborhood, fa.postal_code ? `C.P. ${fa.postal_code}` : ""].filter(Boolean).join(", ");
                const line3 = [fa.municipality, fa.state, fa.country].filter(Boolean).join(", ");
                return (
                  <div className="text-xs text-slate-700 space-y-0.5">
                    {line1 && <p className="font-medium">{line1}</p>}
                    {line2 && <p>{line2}</p>}
                    {line3 && <p>{line3}</p>}
                  </div>
                );
              })()
            )}
          </div>
        </div>

        {/* Right: Analytics */}
        <div className="space-y-4">
          {/* Purchase Summary */}
          {analytics && (
            <div className="bg-white border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><ChartBar size={16} className="text-amber-600" /> Historial de Compras</h3>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-6 text-[10px] text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => handleExportHistory("excel")} data-testid="export-history-excel">Excel</Button>
                  <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleExportHistory("pdf")} data-testid="export-history-pdf">PDF</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-sky-50 rounded-lg p-3 text-center">
                  <p className="text-[9px] text-sky-500 uppercase font-bold">Total Comprado</p>
                  <p className="text-lg font-extrabold text-sky-700 font-mono">{fmtMoney(analytics.total_spent)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-[9px] text-slate-400 uppercase font-bold">Ordenes</p>
                  <p className="text-lg font-extrabold text-slate-700">{analytics.total_orders}</p>
                </div>
              </div>

              {/* Top Categories */}
              {analytics.top_categories.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-slate-600 mb-2">Categorias mas compradas</p>
                  <div className="space-y-1.5">
                    {analytics.top_categories.map((cat, i) => {
                      const maxCat = analytics.top_categories[0]?.total || 1;
                      const pct = (cat.total / maxCat) * 100;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-500 w-28 truncate">{cat.category}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                            <div className="bg-amber-500 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] font-mono font-bold text-slate-700 w-20 text-right">{fmtMoney(cat.total)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top Products */}
              {analytics.top_products.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-600 mb-2">Productos mas comprados</p>
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {analytics.top_products.map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-800 truncate">{p.name}</p>
                          <p className="text-[10px] text-slate-400">{p.qty} unidades</p>
                        </div>
                        <span className="text-xs font-mono font-bold text-slate-700 shrink-0">{fmtMoney(p.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analytics.top_products.length === 0 && analytics.top_categories.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">Sin historial de compras</p>
              )}
            </div>
          )}

          {/* Client Info */}
          <div className="bg-white border rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5"><Buildings size={16} className="text-slate-500" /> Informacion General</h3>
            <div className="space-y-2 text-xs text-slate-600">
              {client.contact_name && <p className="flex items-center gap-1.5"><User size={13} /> {client.contact_name}</p>}
              {client.phone && <p className="flex items-center gap-1.5"><Phone size={13} /> {client.phone}</p>}
              {client.email && <p className="flex items-center gap-1.5"><Envelope size={13} /> {client.email}</p>}
              {(client.city || client.state) && <p className="flex items-center gap-1.5"><Buildings size={13} /> {[client.city, client.state].filter(Boolean).join(", ")}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase font-bold mb-0.5">{label}</p>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}
